/**
 * Curriculum Data Module
 *
 * Provides country definitions, grade-to-internal-level mappings,
 * school-level unit sets, and topic-based unit sets.
 *
 * Internal leveling system (1–12) represents an approximate average of
 * content typically studied at each grade level worldwide.  Country-specific
 * overrides supply localised unit titles / descriptions when available.
 *
 * To add a new country or update curricula, edit the relevant export below
 * without changing the component code.
 */

// ---------------------------------------------------------------------------
// Countries
// ---------------------------------------------------------------------------
export const countries = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
];

// ---------------------------------------------------------------------------
// Grades per country  →  mapped to an internal level (1–12)
// ---------------------------------------------------------------------------
export const gradesByCountry = {
  US: [
    { id: 'us-6', name: '6th Grade', level: 6 },
    { id: 'us-7', name: '7th Grade', level: 7 },
    { id: 'us-8', name: '8th Grade', level: 8 },
    { id: 'us-9', name: '9th Grade (Freshman)', level: 9 },
    { id: 'us-10', name: '10th Grade (Sophomore)', level: 10 },
    { id: 'us-11', name: '11th Grade (Junior)', level: 11 },
    { id: 'us-12', name: '12th Grade (Senior)', level: 12 },
  ],
  FR: [
    { id: 'fr-6eme', name: '6ème', level: 6 },
    { id: 'fr-5eme', name: '5ème', level: 7 },
    { id: 'fr-4eme', name: '4ème', level: 8 },
    { id: 'fr-3eme', name: '3ème', level: 9 },
    { id: 'fr-seconde', name: 'Seconde', level: 10 },
    { id: 'fr-premiere', name: 'Première', level: 11 },
    { id: 'fr-terminale', name: 'Terminale', level: 12 },
  ],
  UK: [
    { id: 'uk-7', name: 'Year 7', level: 6 },
    { id: 'uk-8', name: 'Year 8', level: 7 },
    { id: 'uk-9', name: 'Year 9', level: 8 },
    { id: 'uk-10', name: 'Year 10 (GCSE)', level: 9 },
    { id: 'uk-11', name: 'Year 11 (GCSE)', level: 10 },
    { id: 'uk-12', name: 'Year 12 (A-Level)', level: 11 },
    { id: 'uk-13', name: 'Year 13 (A-Level)', level: 12 },
  ],
  DE: [
    { id: 'de-6', name: 'Klasse 6', level: 6 },
    { id: 'de-7', name: 'Klasse 7', level: 7 },
    { id: 'de-8', name: 'Klasse 8', level: 8 },
    { id: 'de-9', name: 'Klasse 9', level: 9 },
    { id: 'de-10', name: 'Klasse 10', level: 10 },
    { id: 'de-11', name: 'Klasse 11', level: 11 },
    { id: 'de-12', name: 'Klasse 12 (Abitur)', level: 12 },
  ],
  IT: [
    { id: 'it-1media', name: '1ª Media', level: 6 },
    { id: 'it-2media', name: '2ª Media', level: 7 },
    { id: 'it-3media', name: '3ª Media', level: 8 },
    { id: 'it-1sup', name: '1° Superiore', level: 9 },
    { id: 'it-2sup', name: '2° Superiore', level: 10 },
    { id: 'it-3sup', name: '3° Superiore', level: 11 },
    { id: 'it-4sup', name: '4° Superiore', level: 12 },
  ],
  ES: [
    { id: 'es-1eso', name: '1º ESO', level: 6 },
    { id: 'es-2eso', name: '2º ESO', level: 7 },
    { id: 'es-3eso', name: '3º ESO', level: 8 },
    { id: 'es-4eso', name: '4º ESO', level: 9 },
    { id: 'es-1bach', name: '1º Bachillerato', level: 10 },
    { id: 'es-2bach', name: '2º Bachillerato', level: 11 },
    { id: 'es-selectividad', name: 'Selectividad / EvAU', level: 12 },
  ],
};

// ---------------------------------------------------------------------------
// Default (general) units per internal level
// ---------------------------------------------------------------------------
export const defaultLevelUnits = {
  6: [
    { id: 'l6-1', title: 'Fractions & Decimals', description: 'Operations with fractions, decimals, and conversions between them.' },
    { id: 'l6-2', title: 'Ratios & Proportions', description: 'Understanding ratios, rates, and proportional reasoning.' },
    { id: 'l6-3', title: 'Introduction to Algebra', description: 'Variables, simple expressions, and one-step equations.' },
    { id: 'l6-4', title: 'Geometry Basics', description: 'Area, perimeter, and basic properties of shapes.' },
    { id: 'l6-5', title: 'Data & Statistics', description: 'Mean, median, mode, and basic data representation.' },
  ],
  7: [
    { id: 'l7-1', title: 'Integers & Rational Numbers', description: 'Operations with negative numbers and the number line.' },
    { id: 'l7-2', title: 'Expressions & Equations', description: 'Two-step equations, inequalities, and simplification.' },
    { id: 'l7-3', title: 'Proportional Relationships', description: 'Constant of proportionality, graphs, and tables.' },
    { id: 'l7-4', title: 'Geometry: Angles & Triangles', description: 'Angle relationships, triangle properties, and constructions.' },
    { id: 'l7-5', title: 'Probability', description: 'Simple and compound probability, experimental vs. theoretical.' },
  ],
  8: [
    { id: 'l8-1', title: 'Linear Equations & Functions', description: 'Slope, y-intercept, graphing linear equations.' },
    { id: 'l8-2', title: 'Systems of Equations', description: 'Solving systems by graphing, substitution, and elimination.' },
    { id: 'l8-3', title: 'Exponents & Radicals', description: 'Laws of exponents, scientific notation, and square roots.' },
    { id: 'l8-4', title: 'Pythagorean Theorem', description: 'Right triangles, distance formula, and applications.' },
    { id: 'l8-5', title: 'Transformations & Congruence', description: 'Translations, rotations, reflections, and congruent figures.' },
  ],
  9: [
    { id: 'l9-1', title: 'Polynomials & Factoring', description: 'Adding, multiplying, and factoring polynomial expressions.' },
    { id: 'l9-2', title: 'Quadratic Functions', description: 'Graphing parabolas, vertex form, and solving quadratics.' },
    { id: 'l9-3', title: 'Inequalities & Absolute Value', description: 'Compound inequalities, absolute value equations and graphs.' },
    { id: 'l9-4', title: 'Coordinate Geometry', description: 'Midpoint, distance, parallel and perpendicular lines.' },
    { id: 'l9-5', title: 'Introduction to Functions', description: 'Domain, range, function notation, and basic transformations.' },
  ],
  10: [
    { id: 'l10-1', title: 'Advanced Algebra', description: 'Rational expressions, complex fractions, and radical equations.' },
    { id: 'l10-2', title: 'Trigonometry Fundamentals', description: 'Sine, cosine, tangent, and the unit circle.' },
    { id: 'l10-3', title: 'Euclidean Geometry Proofs', description: 'Formal proofs, congruence, and similarity theorems.' },
    { id: 'l10-4', title: 'Sequences & Patterns', description: 'Arithmetic and geometric sequences, recursive formulas.' },
    { id: 'l10-5', title: 'Statistics & Data Analysis', description: 'Standard deviation, normal distribution basics, and regression.' },
  ],
  11: [
    { id: 'l11-1', title: 'Logarithmic & Exponential Functions', description: 'Properties of logarithms, exponential growth and decay.' },
    { id: 'l11-2', title: 'Systems of Equations (Advanced)', description: 'Three-variable systems, matrices introduction.' },
    { id: 'l11-3', title: 'Trigonometry (Advanced)', description: 'Identities, equations, law of sines and cosines.' },
    { id: 'l11-4', title: 'Conic Sections', description: 'Circles, ellipses, parabolas, and hyperbolas.' },
    { id: 'l11-5', title: 'Sequences & Series', description: 'Convergence, sigma notation, arithmetic and geometric series.' },
    { id: 'l11-6', title: 'Introduction to Limits', description: 'Concept of a limit, continuity, and basic limit laws.' },
  ],
  12: [
    { id: 'l12-1', title: 'Derivatives', description: 'Definition, rules of differentiation, and applications.' },
    { id: 'l12-2', title: 'Integrals', description: 'Antiderivatives, definite integrals, and the Fundamental Theorem.' },
    { id: 'l12-3', title: 'Probability & Combinatorics', description: 'Permutations, combinations, conditional probability.' },
    { id: 'l12-4', title: 'Complex Numbers', description: 'Operations, polar form, and De Moivre\'s theorem.' },
    { id: 'l12-5', title: 'Vectors & Matrices', description: 'Vector operations, dot product, matrix algebra.' },
    { id: 'l12-6', title: 'Differential Equations (Intro)', description: 'First-order ODEs, separable equations, and applications.' },
  ],
};

// ---------------------------------------------------------------------------
// Country-specific overrides  (supplement / replace default units for a level)
// ---------------------------------------------------------------------------
export const countryLevelOverrides = {
  FR: {
    11: [
      { id: 'fr-11-1', title: 'Fonctions trinômes du second degré', description: 'Étude complète des fonctions polynômes de degré 2.' },
      { id: 'fr-11-2', title: 'Suites arithmétiques et géométriques', description: 'Définitions, propriétés et comportement asymptotique.' },
      { id: 'fr-11-3', title: 'Trigonométrie', description: 'Cercle trigonométrique, formules d\'addition, équations.' },
      { id: 'fr-11-4', title: 'Dérivation', description: 'Nombre dérivé, fonction dérivée, applications.' },
      { id: 'fr-11-5', title: 'Probabilités conditionnelles', description: 'Arbres pondérés, formule de Bayes, indépendance.' },
      { id: 'fr-11-6', title: 'Produit scalaire', description: 'Définition, propriétés, applications géométriques.' },
    ],
    12: [
      { id: 'fr-12-1', title: 'Limites et continuité', description: 'Limites de fonctions, théorèmes de comparaison, continuité.' },
      { id: 'fr-12-2', title: 'Fonction logarithme népérien', description: 'Propriétés, dérivée, équations et inéquations.' },
      { id: 'fr-12-3', title: 'Fonction exponentielle', description: 'Propriétés, dérivée, croissances comparées.' },
      { id: 'fr-12-4', title: 'Intégration', description: 'Primitives, intégrales, calcul d\'aires.' },
      { id: 'fr-12-5', title: 'Probabilités — Lois continues', description: 'Loi uniforme, loi exponentielle, loi normale.' },
      { id: 'fr-12-6', title: 'Géométrie dans l\'espace', description: 'Droites et plans, orthogonalité, distances.' },
    ],
  },
  UK: {
    11: [
      { id: 'uk-11-1', title: 'Pure Mathematics: Algebra & Functions', description: 'Partial fractions, modulus, composite functions.' },
      { id: 'uk-11-2', title: 'Coordinate Geometry in the (x, y) Plane', description: 'Parametric equations, curves, and loci.' },
      { id: 'uk-11-3', title: 'Trigonometry (A-Level)', description: 'Radians, sec/csc/cot, addition formulae.' },
      { id: 'uk-11-4', title: 'Differentiation Techniques', description: 'Chain rule, product rule, quotient rule, implicit differentiation.' },
      { id: 'uk-11-5', title: 'Integration Techniques', description: 'By substitution, by parts, partial fractions.' },
      { id: 'uk-11-6', title: 'Vectors', description: '2D/3D vectors, scalar product, equations of lines.' },
    ],
    12: [
      { id: 'uk-12-1', title: 'Further Algebra', description: 'Binomial expansion, series, method of differences.' },
      { id: 'uk-12-2', title: 'Further Calculus', description: 'Differential equations, volumes of revolution.' },
      { id: 'uk-12-3', title: 'Numerical Methods', description: 'Newton-Raphson, trapezium rule, iterative methods.' },
      { id: 'uk-12-4', title: 'Statistics', description: 'Hypothesis testing, correlation, regression.' },
      { id: 'uk-12-5', title: 'Mechanics', description: 'Forces, moments, kinematics, Newton\'s laws.' },
    ],
  },
  DE: {
    11: [
      { id: 'de-11-1', title: 'Analysis: Funktionen & Ableitungen', description: 'Differentialrechnung, Kurvendiskussion.' },
      { id: 'de-11-2', title: 'Analytische Geometrie', description: 'Vektoren, Geraden und Ebenen im Raum.' },
      { id: 'de-11-3', title: 'Stochastik', description: 'Bedingte Wahrscheinlichkeit, Binomialverteilung.' },
    ],
    12: [
      { id: 'de-12-1', title: 'Integralrechnung', description: 'Stammfunktionen, bestimmte Integrale, Flächenberechnung.' },
      { id: 'de-12-2', title: 'Analytische Geometrie (Abitur)', description: 'Abstände, Schnittwinkel, Kreise und Kugeln.' },
      { id: 'de-12-3', title: 'Stochastik (Abitur)', description: 'Normalverteilung, Hypothesentests.' },
    ],
  },
  IT: {
    11: [
      { id: 'it-11-1', title: 'Limiti e continuità', description: 'Concetto di limite, teoremi sui limiti, continuità.' },
      { id: 'it-11-2', title: 'Derivate', description: 'Regole di derivazione, studio di funzione.' },
      { id: 'it-11-3', title: 'Trigonometria', description: 'Funzioni goniometriche, equazioni e disequazioni.' },
      { id: 'it-11-4', title: 'Probabilità e statistica', description: 'Calcolo combinatorio, distribuzioni di probabilità.' },
    ],
  },
  ES: {
    11: [
      { id: 'es-11-1', title: 'Análisis: Límites y derivadas', description: 'Concepto de límite, reglas de derivación, aplicaciones.' },
      { id: 'es-11-2', title: 'Álgebra lineal', description: 'Matrices, determinantes, sistemas de ecuaciones.' },
      { id: 'es-11-3', title: 'Geometría analítica', description: 'Vectores, rectas y planos en el espacio.' },
      { id: 'es-11-4', title: 'Probabilidad y estadística', description: 'Distribuciones, inferencia estadística.' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Topic-based units (independent of school curriculum)
// ---------------------------------------------------------------------------
export const topicUnits = [
  {
    id: 'topic-algebra',
    title: 'Algebra',
    description: 'From basic equations to abstract algebra — a comprehensive journey through algebraic structures.',
    icon: '🔤',
    category: 'foundations',
  },
  {
    id: 'topic-geometry',
    title: 'Geometry',
    description: 'Euclidean geometry, coordinate geometry, transformations, and geometric proofs.',
    icon: '📐',
    category: 'foundations',
  },
  {
    id: 'topic-trigonometry',
    title: 'Trigonometry',
    description: 'Trigonometric functions, identities, equations, and their geometric interpretations.',
    icon: '📏',
    category: 'foundations',
  },
  {
    id: 'topic-number-theory',
    title: 'Number Theory',
    description: 'Primes, divisibility, modular arithmetic, and Diophantine equations.',
    icon: '🔢',
    category: 'foundations',
  },
  {
    id: 'topic-derivatives',
    title: 'Derivatives',
    description: 'Limits, differentiation rules, curve sketching, optimization, and related rates.',
    icon: '📈',
    category: 'calculus',
  },
  {
    id: 'topic-integrals',
    title: 'Integrals',
    description: 'Antiderivatives, definite integrals, techniques of integration, and applications.',
    icon: '∫',
    category: 'calculus',
  },
  {
    id: 'topic-series',
    title: 'Sequences & Series',
    description: 'Arithmetic, geometric, and power series — convergence tests and applications.',
    icon: 'Σ',
    category: 'calculus',
  },
  {
    id: 'topic-diff-eq',
    title: 'Differential Equations',
    description: 'First and second-order ODEs, separable, linear, and applications in modeling.',
    icon: '🧮',
    category: 'calculus',
  },
  {
    id: 'topic-linear-algebra',
    title: 'Linear Algebra',
    description: 'Vectors, matrices, determinants, eigenvalues, and vector spaces.',
    icon: '🔲',
    category: 'advanced',
  },
  {
    id: 'topic-probability',
    title: 'Probability & Statistics',
    description: 'Combinatorics, distributions, hypothesis testing, and statistical inference.',
    icon: '🎲',
    category: 'applied',
  },
  {
    id: 'topic-complex',
    title: 'Complex Numbers',
    description: 'Arithmetic, polar form, roots of unity, and conformal mappings.',
    icon: '💠',
    category: 'advanced',
  },
  {
    id: 'topic-logic',
    title: 'Logic & Proofs',
    description: 'Propositional logic, proof techniques, set theory, and mathematical reasoning.',
    icon: '🧩',
    category: 'foundations',
  },
];

// ---------------------------------------------------------------------------
// Topic categories (for grouping in the UI)
// ---------------------------------------------------------------------------
export const topicCategories = [
  { id: 'foundations', label: 'Foundations' },
  { id: 'calculus', label: 'Calculus' },
  { id: 'advanced', label: 'Advanced' },
  { id: 'applied', label: 'Applied' },
];

// ---------------------------------------------------------------------------
// Helper: get units for a given country + level
// ---------------------------------------------------------------------------
export function getUnitsForLevel(countryCode, level) {
  const overrides = countryLevelOverrides[countryCode]?.[level];
  if (overrides) return overrides;
  return defaultLevelUnits[level] || [];
}
